import React, {useEffect, useState} from 'react';
import {Person} from '../types/Person';
import {Workshop} from '../types/Workshop';
import {UpdatePersonAssignment, UpdateWorkshopAssignment} from '../types/ArtisticObject';
import peopleService from '../services/peopleService';
import workshopsService from '../services/workshopsService';
import {AdminButton, AdminInputCheckbox, AdminInputDropdown, AdminInputRadio} from '../../../layout/admin';
import {Button} from 'primereact/button';
import {handleError} from "../../../common/utils/errorHandling";
import {Dialog} from "primereact/dialog";
import ArtisanTextField from "./ArtisanTextField";
import {useTranslation} from "react-i18next";

interface ArtisanAssignmentsProps {
  personAssignments: UpdatePersonAssignment[];
  workshopAssignments: UpdateWorkshopAssignment[];
  onPersonAssignmentsChange: (assignments: UpdatePersonAssignment[]) => void;
  onWorkshopAssignmentsChange: (assignments: UpdateWorkshopAssignment[]) => void;
  creatorsText: string;
  designersText: string;
  onCreatorsChange: (value: string) => void;
  onDesignersChange: (value: string) => void;
}

const ArtisanAssignments: React.FC<ArtisanAssignmentsProps> = ({
                                                                 personAssignments,
                                                                 workshopAssignments,
                                                                 onPersonAssignmentsChange,
                                                                 onWorkshopAssignmentsChange,
                                                                 creatorsText,
                                                                 designersText,
                                                                 onCreatorsChange,
                                                                 onDesignersChange
                                                               }) => {
  const {t} = useTranslation();
  const [people, setPeople] = useState<Person[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingType, setEditingType] = useState<'person' | 'workshop'>('person');
  const [assignmentType, setAssignmentType] = useState<'person' | 'workshop'>('person');
  const [formData, setFormData] = useState({
    personId: '',
    workshopId: '',
    type: 'creator',
    uncertain: false
  });

  useEffect(() => {
    fetchPeople();
    fetchWorkshops();
  }, []);

  const fetchPeople = async (): Promise<void> => {
    await handleError(
      async () => {
        const peopleData = await peopleService.getAllPeople();
        setPeople(peopleData);
      },
      t('admin.artisans.errors.fetchingPeople')
    );
  };

  const fetchWorkshops = async (): Promise<void> => {
    await handleError(
      async () => {
        const workshopsData = await workshopsService.getAllWorkshops();
        setWorkshops(workshopsData);
      },
      t('admin.artisans.errors.fetchingWorkshops')
    );
  };

  const openAddDialog = (): void => {
    setEditingIndex(null);
    setEditingType('person');
    setAssignmentType('person');
    setFormData({
      personId: '',
      workshopId: '',
      type: 'creator',
      uncertain: false
    });
    setDialogVisible(true);
  };

  const openEditPersonDialog = (index: number): void => {
    const assignment = personAssignments[index];
    setEditingIndex(index);
    setEditingType('person');
    setAssignmentType('person');
    setFormData({
      personId: assignment.personId,
      workshopId: '',
      type: assignment.type,
      uncertain: assignment.uncertain
    });
    setDialogVisible(true);
  };

  const openEditWorkshopDialog = (index: number): void => {
    const assignment = workshopAssignments[index];
    setEditingIndex(index);
    setEditingType('workshop');
    setAssignmentType('workshop');
    setFormData({
      personId: '',
      workshopId: assignment.workshopId,
      type: assignment.type,
      uncertain: assignment.uncertain
    });
    setDialogVisible(true);
  };

  const handleDialogHide = (): void => {
    setDialogVisible(false);
    setEditingIndex(null);
    setEditingType('person');
    setAssignmentType('person');
    setFormData({
      personId: '',
      workshopId: '',
      type: 'creator',
      uncertain: false
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const {name, value, type} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handlePersonSubmit = () => {
    const newAssignment: UpdatePersonAssignment = {
      personId: formData.personId,
      type: formData.type,
      uncertain: formData.uncertain
    };

    let updatedAssignments: UpdatePersonAssignment[];
    if (editingIndex !== null && editingType === 'person') {
      // Edit existing person assignment
      updatedAssignments = personAssignments.map((assignment, index) =>
        index === editingIndex ? newAssignment : assignment
      );
    } else {
      // Add new person assignment
      updatedAssignments = [...personAssignments, newAssignment];
    }

    onPersonAssignmentsChange(updatedAssignments);
  }

  const handleWorkshopSubmit = () => {
    const newAssignment: UpdateWorkshopAssignment = {
      workshopId: formData.workshopId,
      type: formData.type,
      uncertain: formData.uncertain
    };

    let updatedAssignments: UpdateWorkshopAssignment[];
    if (editingIndex !== null && editingType === 'workshop') {
      // Edit existing workshop assignment
      updatedAssignments = workshopAssignments.map((assignment, index) =>
        index === editingIndex ? newAssignment : assignment
      );
    } else {
      // Add new workshop assignment
      updatedAssignments = [...workshopAssignments, newAssignment];
    }

    onWorkshopAssignmentsChange(updatedAssignments);
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    if (assignmentType === 'person') {
      if (!formData.personId) return;

      handlePersonSubmit();
    } else {
      if (!formData.workshopId) return;

      handleWorkshopSubmit();
    }

    handleDialogHide();
  };

  const handleDeletePerson = (index: number): void => {
    const updatedAssignments = personAssignments.filter((_, i) => i !== index);
    onPersonAssignmentsChange(updatedAssignments);
  };

  const handleDeleteWorkshop = (index: number): void => {
    const updatedAssignments = workshopAssignments.filter((_, i) => i !== index);
    onWorkshopAssignmentsChange(updatedAssignments);
  };

  const personOptions = people.map(person => ({
    label: `${person.name} ${person.surname}`,
    value: person.id
  }));

  const workshopOptions = workshops.map(workshop => ({
    label: workshop.location ? `${workshop.name} (${workshop.location})` : workshop.name,
    value: workshop.id
  }));

  const getPersonName = (personId: string): string => {
    const person = people.find(p => p.id === personId);
    return person ? `${person.name} ${person.surname}` : t('admin.artisans.types.unknownPerson');
  };

  const getWorkshopName = (workshopId: string): string => {
    const workshop = workshops.find(w => w.id === workshopId);
    return workshop ? (workshop.location ? `${workshop.name} (${workshop.location})` : workshop.name) : t('admin.artisans.types.unknownWorkshop');
  };

  // Local component for rendering individual assignments
  const Assignment: React.FC<{
    name: string;
    uncertain: boolean;
    assignmentType: 'person' | 'workshop';
    index: number;
    onEdit: (index: number) => void;
    onDelete: (index: number) => void;
  }> = ({ name, uncertain, assignmentType, index, onEdit, onDelete }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded border">
      <div className="flex-1">
        <span className="font-medium">{name}</span>
        <span className={`text-xs ml-2`}>
          [{assignmentType === 'person' ? t('admin.artisans.types.person') : t('admin.artisans.types.workshop')}]
        </span>
        {uncertain && (
          <i className="text-sm text-gray-400 ml-2 fa fa-circle-question"></i>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          className="hover:text-gray-700"
          type="button"
          icon="fa fa-edit"
          size="small"
          severity="secondary"
          outlined
          onClick={() => onEdit(index)}
        />
        <Button
          className="hover:text-gray-700"
          type="button"
          icon="fa fa-trash"
          size="small"
          severity="danger"
          outlined
          onClick={() => onDelete(index)}
        />
      </div>
    </div>
  );

  // Combine and group assignments by type
  const allAssignments = [
    ...personAssignments.map((assignment, index) => ({
      ...assignment,
      assignmentType: 'person' as const,
      index,
      name: getPersonName(assignment.personId)
    })),
    ...workshopAssignments.map((assignment, index) => ({
      ...assignment,
      assignmentType: 'workshop' as const,
      index,
      name: getWorkshopName(assignment.workshopId)
    }))
  ];

  const creators = allAssignments.filter(a => a.type === 'creator');
  const designers = allAssignments.filter(a => a.type === 'designer');

  return (
    <>
      {/* Assignment List */}
      {(personAssignments.length > 0 || workshopAssignments.length > 0) && (
        <div className="mb-4 space-y-4">
          {/* Creators Section */}
          {creators.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('admin.artisans.sections.creators')}</h4>
              <div className="space-y-2">
                {creators.map((assignment) => (
                  <Assignment
                    key={`${assignment.assignmentType}-${assignment.index}`}
                    name={assignment.name}
                    uncertain={assignment.uncertain}
                    assignmentType={assignment.assignmentType}
                    index={assignment.index}
                    onEdit={assignment.assignmentType === 'person' ? openEditPersonDialog : openEditWorkshopDialog}
                    onDelete={assignment.assignmentType === 'person' ? handleDeletePerson : handleDeleteWorkshop}
                  />
                ))}
                {/* Creators Text Field */}
                <ArtisanTextField
                  label={t('admin.artisans.sections.creators')}
                  value={creatorsText}
                  assignments={creators.map(c => ({
                    type: c.assignmentType,
                    personId: c.assignmentType === 'person' ? c.personId : undefined,
                    workshopId: c.assignmentType === 'workshop' ? c.workshopId : undefined,
                    uncertain: c.uncertain
                  }))}
                  people={people}
                  workshops={workshops}
                  onChange={onCreatorsChange}
                />
              </div>
            </div>
          )}

          {/* Designers Section */}
          {designers.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('admin.artisans.sections.designers')}</h4>
              <div className="space-y-2">
                {designers.map((assignment) => (
                  <Assignment
                    key={`${assignment.assignmentType}-${assignment.index}`}
                    name={assignment.name}
                    uncertain={assignment.uncertain}
                    assignmentType={assignment.assignmentType}
                    index={assignment.index}
                    onEdit={assignment.assignmentType === 'person' ? openEditPersonDialog : openEditWorkshopDialog}
                    onDelete={assignment.assignmentType === 'person' ? handleDeletePerson : handleDeleteWorkshop}
                  />
                ))}
                {/* Designers Text Field */}
                <ArtisanTextField
                  label={t('admin.artisans.sections.designers')}
                  value={designersText}
                  assignments={designers.map(d => ({
                    type: d.assignmentType,
                    personId: d.assignmentType === 'person' ? d.personId : undefined,
                    workshopId: d.assignmentType === 'workshop' ? d.workshopId : undefined,
                    uncertain: d.uncertain
                  }))}
                  people={people}
                  workshops={workshops}
                  onChange={onDesignersChange}
                />
              </div>
            </div>
          )}

        </div>
      )}

      {/* Add Artisan Button */}
      <AdminButton
        label={t('admin.artisans.actions.addArtisan')}
        onClick={openAddDialog}
        variant="grey"
      />

      {/* Dialog */}
      <Dialog
        header={editingIndex !== null ? t('admin.artisans.dialog.editTitle') : t('admin.artisans.dialog.addTitle')}
        visible={dialogVisible}
        style={{width: '50vw'}}
        onHide={handleDialogHide}
        maskClassName="bg-black bg-opacity-50"
      >
        <form>
          {/* Assignment Type Selection - only show for new assignments */}
          {editingIndex === null && (
            <AdminInputRadio
              label={t('admin.artisans.fields.assignmentType')}
              value={assignmentType}
              onChange={type => setAssignmentType(type as 'person' | 'workshop')}
              options={[
                {label: t('admin.artisans.types.person'), value: 'person'},
                {label: t('admin.artisans.types.workshop'), value: 'workshop'}
              ]}
              name="assignmentType"
              id="assignmentType"
              className="mb-4"
            />
          )}

          {/* Person Selection */}
          {assignmentType === 'person' && (
            <AdminInputDropdown
              label={t('admin.artisans.types.person')}
              value={formData.personId}
              onChange={handleInputChange}
              options={personOptions}
              placeholder={t('admin.artisans.placeholders.selectPerson')}
              name="personId"
              id="personId"
              required
              className="mb-4"
            />
          )}

          {/* Workshop Selection */}
          {assignmentType === 'workshop' && (
            <AdminInputDropdown
              label={t('admin.artisans.types.workshop')}
              value={formData.workshopId}
              onChange={handleInputChange}
              options={workshopOptions}
              placeholder={t('admin.artisans.placeholders.selectWorkshop')}
              name="workshopId"
              id="workshopId"
              required
              className="mb-4"
            />
          )}

          <AdminInputDropdown
            label={t('admin.artisans.fields.type')}
            value={formData.type}
            onChange={handleInputChange}
            options={[
              {label: t('admin.artisans.types.creator'), value: 'creator'},
              {label: t('admin.artisans.types.designer'), value: 'designer'}
            ]}
            name="type"
            id="type"
            className="mb-4"
          />

          <AdminInputCheckbox
            label={t('admin.artisans.fields.uncertain')}
            checked={formData.uncertain}
            onChange={handleInputChange}
            name="uncertain"
            id="uncertain"
            className="mb-4 checkbox-grey"
            helpText={t('admin.artisans.help.uncertain')}
          />

          <div className="flex justify-end gap-4 pt-4">
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={handleDialogHide}
              variant="white"
            />
            <AdminButton
              type="submit"
              label={editingIndex !== null ? t('admin.common.actions.update') : t('admin.common.actions.add')}
              variant="black"
              onClick={handleSubmit}
              disabled={assignmentType === 'person' ? !formData.personId : !formData.workshopId}
            />
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default ArtisanAssignments;
